import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { reduce } from 'lodash';

import { AddToCartButton } from '../AddToCartButton';

import './QuickInfoModal.scss';

export function QuickInfoModal({ id, close }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('rareSkins'); // TODO Properly default

  async function fetchHighend(id) {
    setLoading(true);

    const {
      data: { details, skinsAndChampions },
    } = await axios.get('/api/highends/' + id);

    const groupedSkinsAndChampions = reduce(
      skinsAndChampions,
      (data, item) => {
        if (item.type === 'skin' && item.rare) {
          data.rareSkins.push(item);
        } else if (item.type === 'skin') {
          data.skins.push(item);
        } else if (item.type === 'champion') {
          data.champions.push(item);
        }
        return data;
      },
      { rareSkins: [], skins: [], champions: [] }
    );

    setData({ ...groupedSkinsAndChampions, details });
    setTab(
      groupedSkinsAndChampions.rareSkins.length > 0 ? 'rareSkins' : 'skins'
    );
    setLoading(false);
  }

  // Fetch data on mount
  useEffect(
    () => {
      fetchHighend(id);
    },
    [id]
  );

  const regionCardStyle = {};

  return (
    <div v-if="isOpen" className="quick-info-modal-container">
      <div className="backdrop" onClick={close} />
      <div className="modal">
        <div className="h-full w-full relative">
          <div
            className="absolute top-0 right-0 mt-1 mr-2 cursor-pointer"
            onClick={close}
          >
            <i className="fas fa-times" />
          </div>

          {/* Loader */}
          {loading && (
            <div className="h-full w-full flex justify-center items-center">
              <i className="fas fa-circle-notch fa-2x fa-spin text-smurfs-gold" />
            </div>
          )}

          {/* Content */}
          {!loading && (
            <div className="h-full flex items-stretch">
              {/* Sidebar */}
              <div className="sidebar">
                {/* Rare skins tab */}
                {data.rareSkins.length > 0 && (
                  <div
                    onClick={() => setTab('rareSkins')}
                    className="sidebar-item"
                  >
                    <div className="square-icon">
                      <img
                        src={
                          tab === 'rareSkins'
                            ? '/images/highends/icons/diamond_active.jpg'
                            : '/images/highends/icons/diamond.jpg'
                        }
                      />
                    </div>
                    <span className="mt-1">Rare Skins</span>
                  </div>
                )}

                {/* Skins tab */}
                <div onClick={() => setTab('skins')} className="sidebar-item">
                  <div className="square-icon">
                    <img
                      src={
                        tab === 'skins'
                          ? '/images/highends/icons/skins_active.jpg'
                          : '/images/highends/icons/skins.jpg'
                      }
                    />
                  </div>
                  <span className="mt-1">Skin List</span>
                </div>

                {/* Champions tab */}
                <div
                  onClick={() => setTab('champions')}
                  className="sidebar-item"
                >
                  <div className="square-icon">
                    <img
                      src={
                        tab === 'champions'
                          ? '/images/highends/icons/champions_active.jpg'
                          : '/images/highends/icons/champions.jpg'
                      }
                    />
                  </div>
                  <span className="mt-1">Champ List</span>
                </div>
              </div>

              <div className="flex">
                {/* Skin/champs list */}
                <div className="w-1/2">
                  <div className="flex flex-wrap content-start h-full overflow-y-auto pt-6">
                    {data[tab].map((item, index) => (
                      <div key={index} className="item-container">
                        <div className="image-container">
                          <div
                            className="image"
                            style={{
                              backgroundImage: `linear-gradient(rgba(19, 19, 19, 0.1) 0%, rgba(19, 19, 19, 0.7) 100%), url('${
                                item.image_url
                              }')`,
                            }}
                          />
                          <span className="text-shadow font-bold">
                            {item.name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Details */}
                <div className="w-1/2 flex flex-col p-6">
                  <div
                    className="font-bold px-3 rounded text-sm"
                    style={regionCardStyle}
                  >
                    {data.details.region.name} Unranked Account
                  </div>

                  <div className="heading-3 heading-gold font-normal mt-3">
                    {data.details.title}
                  </div>
                  <div className="font-bold mt-3">
                    USD {data.details.price_us}
                  </div>
                  <div className="border-t-2 border-honestly-white my-6" />
                  <div className="text-sm leading-sm">
                    {data.details.description}
                  </div>

                  {/* Icons & values */}
                  <div className="flex-1 flex flex-wrap content-end mt-10">
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/influence_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">BLUE ESSENCE</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.blue_essence}
                        </div>
                      </div>
                    </div>
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/rank_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">ACCOUNT RANK</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.rank.name}
                        </div>
                      </div>
                    </div>
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/rp_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">RIOT POINTS</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.riot_points}
                        </div>
                      </div>
                    </div>
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/skins_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">SKIN COUNT</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.skins_count}
                        </div>
                      </div>
                    </div>
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/level_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">ACCOUNT LEVEL</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.level}
                        </div>
                      </div>
                    </div>
                    <div className="w-1/2 flex items-center mt-4">
                      <div className="square-icon">
                        <img src="/images/highends/icons/champions_active.jpg" />
                      </div>
                      <div className="flex flex-col pl-2">
                        <div className="text-sm leading-sm">CHAMPION COUNT</div>
                        <div className="heading-4 heading-gold leading-sm">
                          {data.details.champions_count}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-between mt-10">
                    <a
                      href={data.details.url}
                      className="btn btn-blue flex-1 mr-4"
                    >
                      <span className="btn-content">More Info</span>
                      <span className="icon">
                        <i className="fas fa-info" aria-hidden="true" />
                      </span>
                    </a>
                    <AddToCartButton
                      product={{
                        type: 'highend',
                        id: data.details.id,
                        description: data.details.title,
                        price: +data.details.price,
                        url: data.details.url,
                      }}
                      className="btn-green flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
